import type { SxProps, TextFieldProps } from "@mui/material";
import { get } from "lodash-es";
import {
	type Control,
	Controller,
	type FieldErrors,
	type FieldPath,
	type FieldValues,
	type PathValue,
} from "react-hook-form";

import { StyledFormControl } from "Forms/Components/FormStyles";
import {
	BlTextInput,
	type MaskBlockType,
	type MaskType,
} from "Shared/Inputs/BlTextInput";

export type BLFormInputProps<FormType extends FieldValues> = Omit<
	TextFieldProps,
	"onChange" | "defaultValue"
> & {
	name: FieldPath<FormType>;
	label?: string;
	control: Control<FormType, object>;
	errors: FieldErrors<FormType>;
	maxLength?: number;
	disabled?: boolean;
	mask?: MaskType;
	maskBlocks?: MaskBlockType;
	lazy?: boolean;
	type?: string;
	accept?: string;
	max?: number;
	min?: number;
	scale?: number;
	isNumber?: boolean;
	formControlClass?: "long" | "medium" | "tiny" | string;
	unit?: string;
	inputEndAdornment?: React.JSX.Element;
	inputStartAdornment?: React.JSX.Element;
	hasFilterStyle?: boolean;
	unmask?: boolean | "typed";
	thousandsSeparator?: string;
	labelEndAdornment?: React.JSX.Element;
	labelTooltip?: string;
	disableNumberArrows?: boolean;
	uppercase?: boolean;
	containerSx?: SxProps;
	shouldUnregister?: boolean | undefined;
	defaultValue?: PathValue<FormType, FieldPath<FormType>> | undefined;
	onChange?: (
		event:
			| React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
			| unknown,
	) => void;
};

export const BlFormInput = <T extends object>(props: BLFormInputProps<T>) => {
	const {
		name,
		control,
		errors,
		label,
		type,
		isNumber,
		formControlClass,
		uppercase,
		shouldUnregister,
		defaultValue,
		...rest
	} = props;

	const transform =
		isNumber || type === "number"
			? {
					input: (value: number) => {
						if (value === 0) return "0";
						return !value || Number.isNaN(value) ? "" : value.toString();
					},
					output: (e: { target: { value: string } }) => {
						const output = Number(e.target.value);
						return Number.isNaN(output) ? 0 : output;
					},
				}
			: {
					input: (value: any) =>
						value === null || value === undefined ? "" : value,
					output: (e: { target: { value: string } }) => {
						return e.target.value;
					},
				};

	return (
		<Controller
			control={control}
			name={name}
			shouldUnregister={shouldUnregister}
			defaultValue={defaultValue}
			render={({ field: { onChange, value, onBlur, ref } }) => {
				return (
					<StyledFormControl
						className={`${formControlClass} ${!label ? "no-label" : ""}`}
						sx={{ display: rest.hidden ? "none" : undefined }}
					>
						<BlTextInput
							inputRef={ref}
							value={transform.input(value as any)}
							name={name.toString()}
							label={label}
							fieldError={get(errors, name as string)}
							onBlur={onBlur}
							type={type ?? "text"}
							onInputChange={(e) => {
								const val = uppercase
									? e.currentTarget.value.toUpperCase()
									: e.currentTarget.value;
								onChange(val);
								rest.onChange?.(val);
							}}
							onMaskInputChange={(v) => {
								const val = uppercase ? v.toUpperCase() : v;
								const transformedVal = isNumber
									? v === ""
										? null
										: Number(v)
									: val;
								onChange(transformedVal);
								rest.onChange?.(transformedVal);
							}}
							{...rest}
						/>
					</StyledFormControl>
				);
			}}
		/>
	);
};
